import type {
    PrhHolidayHomeOnsaDTO,
    PrhHouseOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/house';
import { type RowInformation } from '../../../../config/flavourConfig';
import ViewHippoInformationModal from '../../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';
import { COVERABLE_CODE } from '../../../../constants/coverable';

export const getBBRtableConfig = (
    coverable: PrhHouseOnsaDTO | PrhHolidayHomeOnsaDTO,
    getHippoText: (resourceKey: string) => string
): RowInformation[] => {
    if (coverable.flavourCode === COVERABLE_CODE.prhHouse)
        return getPrhHouseInformationCoverableDetailsConfig(coverable, getHippoText);
    return getPrhHolidayHouseInformationCoverableDetailsConfig(coverable, getHippoText);
};

const getPrhHolidayHouseInformationCoverableDetailsConfig = (
    coverable: PrhHolidayHomeOnsaDTO,
    getHippoText: (resourceKey: string) => string
): RowInformation[] => {
    return [
        {
            name: 'Opførelsesår',
            fieldValue: coverable.constructionYear?.toString()
        },
        {
            name: 'Bebygget areal',
            fieldValue: `${coverable.developedArea?.toString() ?? 0} m2`,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE3'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-BODY3')
                }
            }
        },
        {
            name: 'Udhus- og garageareal',
            fieldValue: `${coverable.garageOuthouseArea?.toString() ?? 0} m2`
        },
        {
            name: 'Etager (ekskl. kælder)',
            fieldValue: coverable.numberOfFloors
        },
        {
            name: 'Kælder',
            fieldValue: coverable.basement,
            hidden: coverable.isApartment
        },
        {
            name: 'Tagtype',
            fieldValue: coverable.roofType
        },
        {
            name: 'Murtype',
            fieldValue: coverable.exteriorWallType,
            hidden: coverable.isApartment,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-STEP-WOODEN-HOUSE-HEADER'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-STEP-WOODEN-SUBTITLE')
                }
            }
        },
        {
            name: 'Antal toiletter',
            fieldValue: coverable.numberOfToilets?.toString()
        },
        {
            name: 'Opvarmning',
            fieldValue: coverable.primaryHeatingType
        },
        {
            name: 'Brændeovn/Pejs',
            fieldValue: coverable.fireplace
        },
        {
            name: 'Nedlagt erhverv',
            fieldValue: coverable.closedDownCommercial,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-COMMERCIAL-TITLE'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-COMMERCIAL-BODY')
                }
            }
        },
        {
            name: 'Nedlagt landbrug',
            fieldValue: coverable.closedDownFarm,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-FARM-TITLE'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-FARM-BODY')
                }
            }
        },
        {
            name: 'Fredet eller bevaringsværdigt',
            fieldValue: coverable.listedWorthPreservingType,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-PRESERVED-TITLE'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-PRESERVED-BODY')
                }
            }
        },
        {
            name: 'Vandsikringsanlæg',
            fieldValue: coverable.waterProtectionType,
            labelType: 'modalLink',
            hidden: coverable.isApartment,
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-STEP-WATERPROTECTION-TITLE'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-STEP-WATERPROTECTION-BODY')
                }
            }
        }
    ];
};

const getPrhHouseInformationCoverableDetailsConfig = (
    coverable: PrhHouseOnsaDTO,
    getHippoText: (resourceKey: string) => string
): RowInformation[] => {
    return [
        {
            name: 'Opførelsesår',
            fieldValue: coverable.constructionYear?.toString() ?? ''
        },
        {
            name:
                coverable.basementArea === 0
                    ? getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE1')
                    : getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE2'),
            fieldValue: `${coverable.livingArea?.toString() ?? 0} m2`,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title:
                        coverable.basementArea === 0
                            ? getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE1')
                            : getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE2'),
                    description:
                        coverable.basementArea === 0
                            ? getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-BODY1')
                            : getHippoText('DSS-COVERABLE-INFORMATION- DEVELOPEDAREA-BODY2')
                }
            }
        },
        {
            name: 'Bebygget areal',
            fieldValue: `${coverable.developedArea?.toString() ?? '0'} m2`,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE3'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-BODY3')
                }
            }
        },
        {
            name: 'Udhus- og garageareal',
            fieldValue: `${coverable.garageOuthouseArea?.toString() ?? 0} m2`
        },
        {
            name: 'Etager (ekskl. kælder)',
            fieldValue: coverable.numberOfFloors
        },
        {
            name: 'Kælder',
            fieldValue: coverable.basement,
            hidden: coverable.isApartment
        },
        {
            name: 'Kælderareal',
            fieldValue: `${coverable.basementArea?.toString() ?? 0} m2`,
            hidden: coverable.basement !== 'Ja'
        },
        {
            name: 'Tagtype',
            fieldValue: coverable.roofType
        },
        {
            name: 'Murtype',
            fieldValue: coverable.exteriorWallType,
            hidden: coverable.isApartment,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-STEP-WOODEN-HOUSE-HEADER'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-STEP-WOODEN-SUBTITLE')
                }
            }
        },
        {
            name: 'Antal toiletter',
            fieldValue: coverable.numberOfToilets?.toString()
        },
        {
            name: 'Opvarmning',
            fieldValue: coverable.primaryHeatingType
        },
        {
            name: 'Erhverv på ejendommen',
            fieldValue: coverable.commercialActivitiesInTheBuilding
        },
        {
            name: 'Nedlagt erhverv',
            fieldValue: coverable.closedDownCommercial,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-COMMERCIAL-TITLE'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-COMMERCIAL-BODY')
                }
            }
        },
        {
            name: 'Nedlagt landbrug',
            fieldValue: coverable.closedDownFarm,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-FARM-TITLE'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-FARM-BODY')
                }
            }
        },
        {
            name: 'Fredet eller bevaringsværdigt',
            fieldValue: coverable.listedWorthPreservingType,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-PRESERVED-TITLE'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-PRESERVED-BODY')
                }
            }
        },
        {
            name: 'Vandsikringsanlæg',
            fieldValue: coverable.waterProtectionType,
            labelType: 'modalLink',
            hidden: coverable.isApartment,
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: getHippoText('DSS-COVERABLE-INFORMATION-STEP-WATERPROTECTION-TITLE'),
                    description: getHippoText('DSS-COVERABLE-INFORMATION-STEP-WATERPROTECTION-BODY')
                }
            }
        }
    ];
};
